"use client";

import { ListPlus, Megaphone, Radio, Sparkles } from "lucide-react";
import { USE_CASE_LIST, getUseCaseAvailability } from "./constants";
import { UseCaseConfig, UseCaseSuggestion, UseCaseType } from "./types";

import { cn } from "@punchup/utils/cn";

const USE_CASE_ICON_COMPONENTS: Record<UseCaseType, React.ElementType> = {
  announce_show: Megaphone,
  waitlist_new_show: ListPlus,
  send_blast: Radio,
};

function getUseCaseIcon(type: UseCaseType, size: "sm" | "md" = "md") {
  const Icon = USE_CASE_ICON_COMPONENTS[type];
  const sizeClass = size === "sm" ? "h-4 w-4" : "h-6 w-6";
  return <Icon className={sizeClass} />;
}

/** Small icons for use in the collapsed accordion header */
export const USE_CASE_ICONS_SM: Record<UseCaseType, React.ReactNode> = {
  announce_show: getUseCaseIcon("announce_show", "sm"),
  waitlist_new_show: getUseCaseIcon("waitlist_new_show", "sm"),
  send_blast: getUseCaseIcon("send_blast", "sm"),
};

interface UseCaseCardsProps {
  selectedUseCase: UseCaseType | null;
  onSelectUseCase: (useCase: UseCaseType) => void;
  suggestions?: Record<UseCaseType, UseCaseSuggestion | null>;
  isLoading?: boolean;
  compact?: boolean;
}

export function UseCaseCards({
  selectedUseCase,
  onSelectUseCase,
  suggestions,
  isLoading,
  compact,
}: UseCaseCardsProps) {
  return (
    <div className={compact ? "p-1 pb-2" : "pb-4 sm:px-4"}>
      <div
        className={
          compact
            ? "grid grid-cols-2 gap-2"
            : "grid grid-cols-2 gap-2 auto-rows-fr sm:gap-4 md:grid-cols-3 md:gap-4"
        }
      >
        {USE_CASE_LIST.map((config: UseCaseConfig) => {
          const isSelected = selectedUseCase === config.id;
          const suggestion = suggestions?.[config.id] ?? null;
          const alwaysEnabled = config.id === "send_blast";
          const { disabled: isDisabled, reason: disabledReason } =
            getUseCaseAvailability(config.id, suggestion, isLoading);
          return (
            <button
              key={config.id}
              onClick={() => onSelectUseCase(config.id)}
              disabled={isDisabled}
              className={cn(
                "group relative flex h-full flex-col items-start gap-1.5 overflow-hidden rounded-xl border text-left transition-all duration-200",
                compact ? "p-3" : "p-3 sm:p-4 md:p-5",
                isDisabled
                  ? "cursor-not-allowed border-border/50 bg-muted/30 opacity-50"
                  : isSelected
                    ? "border-primary bg-primary/5 shadow-lg hover:scale-[1.03] hover:shadow-xl active:scale-[0.98]"
                    : "border-border bg-gradient-to-br from-card to-accent/5 hover:scale-[1.03] hover:from-accent/10 hover:to-accent/20 hover:shadow-xl active:scale-[0.98]"
              )}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`transition-transform duration-200 group-hover:scale-110 ${
                    isSelected
                      ? "text-primary"
                      : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {getUseCaseIcon(config.id)}
                </span>
                {suggestion?.isSuggested && !isSelected && (
                  <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                    <Sparkles className="h-2.5 w-2.5" />
                    Suggested
                  </span>
                )}
              </div>
              <div className="relative w-full min-w-0">
                <div
                  className={`truncate ${
                    compact
                      ? "text-xs font-semibold"
                      : "text-xs font-semibold sm:text-sm"
                  }`}
                >
                  {config.title}
                </div>
                {!compact && (
                  <p className="mt-0.5 hidden text-xs text-gray-500 sm:block dark:text-gray-400">
                    {config.description}
                  </p>
                )}
              </div>
              <div className="relative mt-auto w-full min-w-0 border-t border-border/50 pt-1.5 sm:pt-2">
                {suggestion && suggestion.count > 0 && !alwaysEnabled ? (
                  <p className="truncate text-xs font-medium text-primary">
                    {suggestion.label}
                  </p>
                ) : (
                  <p className="line-clamp-2 text-xs text-gray-400 dark:text-gray-500">
                    {alwaysEnabled && suggestion?.label
                      ? suggestion.label
                      : config.id === "send_blast"
                        ? "Reach everyone or just your subscribers."
                        : disabledReason}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
